import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import adminApi, { downloadCsv, readableError } from '../utils/adminApi';

/**
 * Drives an admin list screen: querying, filtering, sorting, paging,
 * row selection, bulk actions and CSV export for one resource.
 *
 * `resource` is the API path segment, e.g. 'cvs'. `dataKey` is the property
 * the list lives under in the response, which matches the resource name.
 */
const useAdminResource = ({ resource, dataKey = resource, defaultLimit = 10 }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: defaultLimit, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ sortBy: 'createdAt', sortDir: 'desc' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [selected, setSelected] = useState([]);

  // Debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Any change to the query resets to the first page.
  useEffect(() => { setPage(1); }, [debouncedSearch, status, limit]);

  const params = useMemo(() => ({
    page,
    limit,
    ...(status !== 'all' ? { status } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    sortBy: sort.sortBy,
    sortDir: sort.sortDir
  }), [page, limit, status, debouncedSearch, sort]);

  // Guards against an earlier, slower request overwriting a newer response.
  const requestId = useRef(0);

  const fetchRows = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const [list, stats] = await Promise.all([
        adminApi.get(`/admin/${resource}`, { params }),
        adminApi.get(`/admin/${resource}/stats`)
      ]);
      if (id !== requestId.current) return;
      setRows(list.data?.data?.[dataKey] || []);
      setPagination(list.data?.data?.pagination || { page: 1, limit, total: 0, pages: 1 });
      setCounts(stats.data?.data || { all: 0 });
    } catch (err) {
      if (id !== requestId.current) return;
      setError(readableError(err, 'Could not load records.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [resource, dataKey, params, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Drop selections that are no longer on screen.
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => rows.some((r) => r.id === id)));
  }, [rows]);

  const toggleSort = useCallback((column) => {
    setSort((prev) => prev.sortBy === column
      ? { sortBy: column, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }
      : { sortBy: column, sortDir: 'asc' });
  }, []);

  const toggleRow = useCallback((id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => (prev.length === rows.length ? [] : rows.map((r) => r.id)));
  }, [rows]);

  const updateRow = useCallback(async (id, patch, successMessage = 'Updated') => {
    try {
      await adminApi.put(`/admin/${resource}/${id}`, patch);
      toast.success(successMessage);
      await fetchRows();
      return true;
    } catch (err) {
      toast.error(readableError(err, 'Update failed.'));
      return false;
    }
  }, [resource, fetchRows]);

  const deleteRow = useCallback(async (id) => {
    try {
      await adminApi.delete(`/admin/${resource}/${id}`);
      toast.success('Deleted');
      await fetchRows();
      return true;
    } catch (err) {
      toast.error(readableError(err, 'Delete failed.'));
      return false;
    }
  }, [resource, fetchRows]);

  const bulkAction = useCallback(async (action, value) => {
    if (selected.length === 0) return false;
    try {
      const { data } = await adminApi.post(`/admin/${resource}/bulk`, {
        ids: selected, action, status: value
      });
      toast.success(data.message || 'Done');
      setSelected([]);
      await fetchRows();
      return true;
    } catch (err) {
      toast.error(readableError(err, 'Bulk action failed.'));
      return false;
    }
  }, [resource, selected, fetchRows]);

  const exportCsv = useCallback(async () => {
    try {
      // Export follows the current filters, minus paging.
      const { page: _p, limit: _l, ...filters } = params;
      await downloadCsv(
        `/admin/${resource}/export`,
        filters,
        `${resource}-${new Date().toISOString().slice(0, 10)}.csv`
      );
      toast.success('Export downloaded');
    } catch (err) {
      toast.error(readableError(err, 'Export failed.'));
    }
  }, [resource, params]);

  return {
    rows, counts, pagination, loading, error,
    search, setSearch,
    status, setStatus,
    sort, toggleSort,
    page, setPage,
    limit, setLimit,
    selected, toggleRow, toggleAll, clearSelection: () => setSelected([]),
    refresh: fetchRows,
    updateRow, deleteRow, bulkAction, exportCsv
  };
};

export default useAdminResource;
